import { NgFor } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
  DialogComponent,
} from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';

import { PostManualDeliveryRequest } from 'src/app/delivery/models/flock-registration-service.model';
import { upsertManualDeliveryRequest } from 'src/app/delivery/store/actions/delivery.actions';
import { getDeliveries } from 'src/app/delivery/store/selectors/delivery.selectors';
import { VatPercentages } from 'src/app/shared/constants';
import Utils from 'src/app/shared/utils';

import { Cycle } from '../../models/cycle.model';
import * as cycleActions from '../../store/actions/cycle.actions';
import { getSelectedCycle } from '../../store/selectors/cycle.selectors';
import { FinalSetupDataRow, FinalSetupRowType } from '../final-setup/models/final-setup.model';

export interface FinalSetupEditItemDialogInputData extends AbstractDialogData {
  finalSetupRow: FinalSetupDataRow;
  cycleId: number;
}

@Component({
  selector: 'app-final-setup-edit-item.component',
  templateUrl: 'final-setup-edit-item.component.html',
  imports: [
    DialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    AgrNumberDirective,
    MatSelect,
    MatOption,
    NgFor,
    TranslatePipe,
  ],
})
export class FinalSetupEditItemComponent extends AbstractDialog implements OnInit {
  data = inject<FinalSetupEditItemDialogInputData>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<MatDialogRef<FinalSetupEditItemComponent, AbstractDialogResultData>>(MatDialogRef);
  private readonly localeService = inject(LocaleService);
  private readonly store = inject<Store<RootState>>(Store);

  id: number;
  vatPercentages = VatPercentages;
  finalSetupDataRow = this.data.finalSetupRow;

  formGroup = new FormGroup({
    invoiceDate: new FormControl<Date>(null, []),
    invoiceNumber: new FormControl<string>(null, []),
    amount: new FormControl<number>(null, []),
    vatPercentage: new FormControl<number>(null, []),
  });

  private readonly selectedCycle$: Observable<Cycle> = this.store.select(getSelectedCycle);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    super();
    this.data.title = this.localeService.translate('edit');
    this.id = this.data.finalSetupRow.id;
  }

  ngOnInit() {
    if (this.id > 0) {
      this.formGroup.controls.invoiceDate.setValue(this.finalSetupDataRow.invoiceDate);
      this.formGroup.controls.invoiceNumber.setValue(this.finalSetupDataRow.invoiceNumber);
      this.formGroup.controls.amount.setValue(this.finalSetupDataRow.amount);
      this.formGroup.controls.vatPercentage.setValue(this.finalSetupDataRow.vatPercentage);
    }
  }

  save() {
    if (this.formGroup.valid) {
      switch (this.finalSetupDataRow.finalSetupRowType) {
        case FinalSetupRowType.FeedDeliveries:
          this.saveFeedDeliveryRow();

          break;
        case FinalSetupRowType.ChicksDeliveries:
          this.saveChickDeliveryRow();

          break;
        case FinalSetupRowType.Departures:
          this.saveDepartureRow();

          break;
        case FinalSetupRowType.Discounts:
          this.saveDiscountRow();

          break;
      }

      super.confirm();
    }
  }

  private saveFeedDeliveryRow() {
    this.store
      .select(getDeliveries)
      .pipe(take(1))
      .subscribe((deliveries) => {
        const delivery = deliveries.find((delivery) => delivery.id === this.id);

        const manualDeliveryRequest = {
          id: delivery.id,
          deliveryDate: delivery.deliveryDate,
          manualDeliveryLines: [
            {
              ...delivery.deliveryLines[0],
              invoiceDate: Utils.parseDateTimeFromFormValue(
                this.formGroup.controls.invoiceDate.value,
              ),
              externalInvoiceId: this.formGroup.controls.invoiceNumber.value,
              vatPercentage: this.formGroup.controls.vatPercentage.value,
              unitNettoPrice:
                this.formGroup.controls.amount.value / delivery.deliveryLines[0].unitsDelivered,
            },
          ],
        } as PostManualDeliveryRequest;

        this.store.dispatch(
          upsertManualDeliveryRequest({
            manualDeliveryRequest,
          }),
        );
      });
  }

  private saveChickDeliveryRow() {
    this.selectedCycle$.pipe(take(1)).subscribe((selectedCycle) => {
      if (selectedCycle?.dayOldChicks?.length > 0) {
        selectedCycle.dayOldChicks
          .filter((dayOldChick) => dayOldChick.id === this.id)
          .map((dayOldChick) => {
            if (dayOldChick) {
              const chicksPrice = this.calculatePricePerChick(
                dayOldChick.amountOfChicks,
                this.formGroup.controls.amount.value,
              );

              this.store.dispatch(
                cycleActions.editDayOldChick({
                  cycleId: selectedCycle.id,
                  dayOldChick: {
                    ...dayOldChick,
                    chicksPrice,
                    invoiceDate: Utils.parseDateTimeFromFormValue(
                      this.formGroup.controls.invoiceDate.value,
                    ),
                    invoiceNumber: this.formGroup.controls.invoiceNumber.value,
                    vatPercentage: this.formGroup.controls.vatPercentage.value,
                    chicksTotalPrice: this.calculateTotalPrice(
                      dayOldChick.amountOfChicks,
                      chicksPrice,
                    ),
                  },
                }),
              );
            }
          });
      }
    });
  }

  private saveDepartureRow() {
    this.selectedCycle$.pipe(take(1)).subscribe((selectedCycle) => {
      if (selectedCycle?.departures?.length > 0) {
        selectedCycle.departures
          .filter((departure) => departure.id === this.id)
          .map((departure) => {
            if (departure) {
              this.store.dispatch(
                cycleActions.editDeparture({
                  cycleId: selectedCycle.id,
                  departure: {
                    ...departure,
                    invoiceDate: Utils.parseDateTimeFromFormValue(
                      this.formGroup.controls.invoiceDate.value,
                    ),
                    invoiceNumber: this.formGroup.controls.invoiceNumber.value,
                    vatPercentage: this.formGroup.controls.vatPercentage.value,
                    revenue: this.formGroup.controls.amount.value,
                  },
                }),
              );
            }
          });
      }
    });
  }

  private saveDiscountRow() {
    this.selectedCycle$.pipe(take(1)).subscribe((selectedCycle) => {
      if (selectedCycle?.discounts?.length > 0) {
        selectedCycle.discounts
          .filter((discount) => discount.id === this.id)
          .map((discount) => {
            if (discount) {
              this.store.dispatch(
                cycleActions.editDiscount({
                  cycleId: selectedCycle.id,
                  discount: {
                    ...discount,
                    amount: this.formGroup.controls.amount.value,
                    invoiceDate: Utils.parseDateTimeFromFormValue(
                      this.formGroup.controls.invoiceDate.value,
                    ),
                    invoiceNumber: this.formGroup.controls.invoiceNumber.value,
                    vatPercentage: this.formGroup.controls.vatPercentage.value,
                  },
                }),
              );
            }
          });
      }
    });
  }

  private calculatePricePerChick(amountOfChicks: number, totalPrice: number) {
    return totalPrice / amountOfChicks;
  }

  private calculateTotalPrice(amountOfChicks: number, chicksPrice: number) {
    return Math.round(amountOfChicks * chicksPrice * 100) / 100;
  }
}
